import React from "react";
import { Link } from "react-router-dom";
import { Sprout, Beaker, Truck, Handshake, ArrowRight } from "lucide-react";

import { Helmet } from "react-helmet-async";

const AboutPage = () => {
  return (
    <main data-testid="about-page" className="bg-cream">
      <Helmet>
        <title>About Gralon Global — Indian Botanical Export House</title>
        <meta name="description" content="Learn about Gralon Global's journey, our commitment to quality sourcing from India's heartlands, and our mission to bridge the gap between Indian farmers and global wellness brands." />
      </Helmet>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/33851721/pexels-photo-33851721.jpeg"
          alt="Indian farmland"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 h-full flex items-end pb-16">
          <div className="text-cream max-w-3xl">
            <div className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">About Gralon</div>
            <h1 className="font-serif font-light text-5xl lg:text-7xl leading-[1.02]">
              Rooted in tradition.<br />
              <span className="italic text-gold-light">Grown for the world.</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="label-eyebrow">Our Story</div>
            <h2 className="font-serif font-light mt-6 text-4xl lg:text-5xl leading-[1.05] text-ink">
              A bridge between Indian farms and global wellness.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-6 space-y-6 text-ink/85 leading-relaxed text-lg">
            <p>
              Gralon was founded with a single conviction — that the
              world's finest functional foods and herbs are still grown in the
              soils of India. From the psyllium fields of Gujarat to the
              ashwagandha belts of Madhya Pradesh, we work shoulder-to-shoulder
              with growers and processors who share our standards of purity,
              traceability and consistency.
            </p>
            <p>
              Headquartered in Udaipur, Rajasthan, we operate as a focused export
              house — supplying psyllium husk, super seeds and Ayurvedic herbs
              to importers, formulators and brand owners across more than 20
              countries.
            </p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 bg-cream-alt">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="label-eyebrow">How We Operate</div>
          <h2 className="font-serif font-light mt-6 text-4xl lg:text-5xl text-ink max-w-3xl">
            Four pillars, one promise.
          </h2>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-line">
            {[
              { Icon: Sprout, t: "Origin Sourcing", d: "Direct relationships with contract farmers in India's most renowned growing belts." },
              { Icon: Beaker, t: "Lab-Verified Quality", d: "Every lot tested in NABL-accredited labs for purity, microbial load and pesticide residues." },
              { Icon: Truck, t: "Reliable Logistics", d: "FOB / CIF / DDP shipping with documentation handled end-to-end by our trade team." },
              { Icon: Handshake, t: "Long-Term Partnership", d: "Bespoke specifications, dedicated supply contracts and transparent communication." },
            ].map(({ Icon, t, d }) => (
              <div key={t} className="bg-cream p-10">
                <Icon strokeWidth={1.2} className="w-8 h-8 text-forest" />
                <h3 className="font-serif text-2xl mt-6 text-ink">{t}</h3>
                <p className="text-muted mt-3 leading-relaxed text-sm">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-forest-deep text-cream relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center relative">
          <div className="label-eyebrow text-gold">Partner With Us</div>
          <h2 className="font-serif font-light mt-6 text-4xl lg:text-6xl leading-[1.05]">
            Let's build something <span className="italic text-gold-light">enduring.</span>
          </h2>
          <Link
            to="/contact"
            data-testid="about-cta-contact"
            className="mt-12 inline-flex items-center gap-3 px-10 py-4 bg-gold text-forest-deep text-sm tracking-[0.2em] uppercase hover:bg-gold-light transition-colors duration-300"
          >
            Start a Conversation
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
